import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { LoadingButton } from '@/components/ui/loading-button'
import { Badge } from '@/components/ui/Badge'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select'
import { Label } from '../ui/label'
import { Textarea } from '@/components/ui/Textarea'
import {
  Star, Save, ArrowLeft, CheckCircle, Pencil, AlertCircle, Loader2,
  ArrowRight, Check, Sparkles, TrendingUp, Award, PartyPopper,
  Settings, Users, MessageCircle, Target, FileText, BarChart3, DollarSign, UserCheck, User,
  Shield, Lock, Ban, Undo2, Search, ChevronDown, ChevronUp, X, Briefcase, UserRound, UsersRound, Building2, Calendar
} from 'lucide-react'
import { useTenant } from '@/lib/tenant/tenant-provider'
import { useAuth } from '@/hooks/useAuth'
import {
  createEvaluation,
  saveDraftEvaluation,
  submitEvaluation,
  addEvaluationScores,
  getEvaluationScores,
  getEvaluation,
  updateEvaluation,
  listEvaluationItems,
  listEvaluationItemsByRole,
  getEvaluationCategories,
  getEvaluationConfig,
  getMyEmployeeProfile,
  listEmployees,
  listPeerEmployees
} from '@/api/performance'
import { getMyAllReports, getEmployee, listRoles } from '@/api/workforce'
import type { Role } from '@/types/workforce'
import { EmployeeAutocomplete } from '@/components/workforce/EmployeeAutocomplete'
import type {
  Evaluation,
  EvaluationScore,
  EvaluationItem,
  CreateEvaluationScoreData,
  EvaluationCategory,
  EvaluationConfig,
  Employee as PerformanceEmployee
} from '@/types/performance'
import { FormularioAvaliacaoSkeleton } from './FormularioAvaliacaoSkeleton'
import { useEvaluationUIStore } from '@/stores/evaluation-ui-store'
import { getIconComponent } from './icon-utils'
import { CompactScoreInput } from './CompactScoreInput'

// Score labels
const defaultScoreLabels: Record<number, string> = {
  1: 'Insuficiente',
  2: 'Abaixo do esperado',
  3: 'Dentro do esperado',
  4: 'Acima do esperado',
  5: 'Excepcional'
}

// Evaluator type metadata
const evaluatorTypeIcons: Record<string, React.ComponentType<any>> = {
  self: UserRound,
  manager: Briefcase,
  peer: UsersRound,
  hr: Building2,
}

const evaluatorTypeInfo: Record<string, { label: string; desc: string }> = {
  self: { label: 'Autoavaliacao', desc: 'Avalie seu proprio desempenho' },
  manager: { label: 'Gestor', desc: 'Avalie um membro da sua equipe' },
  peer: { label: 'Par', desc: 'Avalie um colega de equipe' },
  hr: { label: 'RH', desc: 'Avaliacao institucional' },
}

// ============================================================
// MAIN COMPONENT
// ============================================================
const FormularioAvaliacao: React.FC = () => {
  const { tenantDomain } = useTenant()
  const { user } = useAuth()
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as { id?: string }
  const evaluationId = searchParams?.id || null

  const isAdmin = user?.role && ['admin', 'ceo', 'diretor-executivo', 'diretor-financeiro', 'vice-presidente', 'diretor-tecnico', 'rh', 'analista-rh'].includes(user.role) || false

  // ============ STATE ============
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0)
  const focusMode = useEvaluationUIStore((s) => s.focusMode)
  const enterFocusMode = useEvaluationUIStore((s) => s.enterFocusMode)
  const exitFocusMode = useEvaluationUIStore((s) => s.exitFocusMode)
  const [categories, setCategories] = useState<EvaluationCategory[]>([])
  const [config, setConfig] = useState<EvaluationConfig | null>(null)
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null)
  const [scores, setScores] = useState<Record<string, CreateEvaluationScoreData>>({})
  const scoresRef = useRef<Record<string, CreateEvaluationScoreData>>({})
  const [evaluationItems, setEvaluationItems] = useState<EvaluationItem[]>([])
  const [applicableItemIds, setApplicableItemIds] = useState<string[]>([])
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null)
  const [employees, setEmployees] = useState<PerformanceEmployee[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDraft, setIsDraft] = useState(true)
  const [overallScore, setOverallScore] = useState<number | null>(null)
  const [evaluatorType, setEvaluatorType] = useState<'self' | 'manager' | 'hr' | 'peer'>('self')
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null)
  const [evaluationPeriod, setEvaluationPeriod] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  })
  const [showCelebration, setShowCelebration] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('')
  const [selectedEmployeeRoleId, setSelectedEmployeeRoleId] = useState<string | null>(null)
  const [selectedEmployeeRoleGroup, setSelectedEmployeeRoleGroup] = useState<string | null>(null)
  const [selectedEmployeeRoleName, setSelectedEmployeeRoleName] = useState<string | null>(null)
  const [selectedEmployeePictureUrl, setSelectedEmployeePictureUrl] = useState<string | null>(null)
  const [loadingSelectedEmployee, setLoadingSelectedEmployee] = useState(false)
  const [availableRoles, setAvailableRoles] = useState<Role[]>([])
  const [hasPeers, setHasPeers] = useState(false)
  const [loadingPeers, setLoadingPeers] = useState(true)

  const mainContentRef = useRef<HTMLDivElement>(null)

  // ============ COMPUTED ============
  const shouldUseSearchMode = user?.role && ['rh', 'analista-rh', 'vice-presidente'].includes(user.role) || false
  const canDoManagerEvaluation = user?.role && ['diretor-executivo', 'diretor-financeiro', 'coordenador', 'diretor-tecnico', 'rh', 'vice-presidente', 'admin'].includes(user.role) || false
  const canDoHREvaluation = user?.role && ['rh'].includes(user.role) || false

  const applicableCategories = useMemo(() => {
    return categories.filter(cat => {
      const itemsForCategory = evaluationItems.filter(item =>
        item.categoryId === cat.categoryId && applicableItemIds.includes(item.itemId)
      )
      return itemsForCategory.length > 0
    })
  }, [categories, evaluationItems, applicableItemIds])

  const allowedEvaluatorTypes = useMemo(() => {
    const types: Array<{ value: 'self' | 'manager' | 'hr' | 'peer'; label: string }> = []

    if (evaluation) {
      const typeLabels: Record<string, string> = {
        self: 'Autoavaliação', manager: 'Avaliação do Gestor', hr: 'Avaliação do RH', peer: 'Avaliação de Par'
      }
      const evalType = evaluation.evaluatorType as 'self' | 'manager' | 'hr' | 'peer'
      types.push({ value: evalType, label: typeLabels[evalType] || evalType })
      return types
    }

    if (user?.employee?.employeeId) types.push({ value: 'self', label: 'Autoavaliação' })
    if (canDoManagerEvaluation) types.push({ value: 'manager', label: 'Avaliação do Gestor' })
    if (canDoHREvaluation) types.push({ value: 'hr', label: 'Avaliação do RH' })
    if (hasPeers) types.push({ value: 'peer', label: 'Avaliação de Par' })

    return types
  }, [user?.employee?.employeeId, canDoManagerEvaluation, canDoHREvaluation, hasPeers, evaluation])

  // ============ HELPERS ============
  const filterItemIdsByRole = useCallback((items: EvaluationItem[], roleId: string | null, roleGroup: string | null): string[] => {
    if (!roleId && !roleGroup) return items.map(item => item.itemId)

    const filtered = items.filter(item => {
      const hasRoles = item.applicableRoles && item.applicableRoles.length > 0
      const hasRoleGroups = (item as any).applicableRoleGroups && (item as any).applicableRoleGroups.length > 0
      if (!hasRoles && !hasRoleGroups) return true
      const matchesRole = roleId && hasRoles && item.applicableRoles.includes(roleId)
      const matchesRoleGroup = roleGroup && hasRoleGroups && (item as any).applicableRoleGroups.includes(roleGroup)
      return matchesRole || matchesRoleGroup
    })
    return filtered.map(item => item.itemId)
  }, [])

  const getItemsForTheme = useCallback((categoryId: string): EvaluationItem[] => {
    return evaluationItems.filter(item =>
      item.categoryId === categoryId && applicableItemIds.includes(item.itemId)
    )
  }, [evaluationItems, applicableItemIds])

  const calculateOverallScore = useCallback((currentScores: Record<string, CreateEvaluationScoreData>) => {
    const scoredItems = Object.values(currentScores).filter(s => !s.skipped && s.score > 0)
    if (scoredItems.length === 0) { setOverallScore(null); return }
    const sum = scoredItems.reduce((total, s) => total + s.score, 0)
    setOverallScore(Math.round((sum / scoredItems.length) * 100) / 100)
  }, [])

  const getCatProgress = useCallback((cat: EvaluationCategory) => {
    const items = getItemsForTheme(cat.categoryId)
    let done = 0
    items.forEach(item => {
      const s = scores[item.itemId]
      if (s && (s.score > 0 || s.skipped)) done++
    })
    return { done, total: items.length }
  }, [getItemsForTheme, scores])

  const getOverallProgress = useCallback(() => {
    let done = 0, total = 0
    applicableCategories.forEach(cat => {
      const p = getCatProgress(cat)
      done += p.done
      total += p.total
    })
    return { done, total }
  }, [applicableCategories, getCatProgress])

  // Find first category with incomplete items
  const findFirstIncompleteCategory = useCallback((currentScores: Record<string, CreateEvaluationScoreData>): number => {
    for (let ci = 0; ci < applicableCategories.length; ci++) {
      const cat = applicableCategories[ci]
      const items = getItemsForTheme(cat.categoryId)
      const allDone = items.every(item => {
        const s = currentScores[item.itemId]
        return s && (s.score > 0 || s.skipped)
      })
      if (!allDone) return ci
    }
    return applicableCategories.length // all done → review
  }, [applicableCategories, getItemsForTheme])

  // ============ DATA LOADING ============
  const loadEmployeesForEvaluatorType = useCallback(async (type: 'self' | 'manager' | 'hr' | 'peer') => {
    if (!tenantDomain) return { items: [] }
    switch (type) {
      case 'self': return { items: [] }
      case 'hr': return { items: [] }
      case 'peer': return await listPeerEmployees(tenantDomain, { limit: 50 })
      case 'manager':
      default: {
        const reportsResponse = await getMyAllReports({ limit: 100 })
        return {
          items: reportsResponse.items.map(emp => ({
            employeeId: emp.employeeId, tenantId: emp.tenantId,
            name: emp.personalInfo?.name || emp.email, email: emp.email,
            role: emp.currentEngagement?.roleName || 'N/A',
            department: emp.currentEngagement?.departmentName || 'N/A',
            managerId: emp.managerId || undefined, managerName: emp.managerName || undefined,
            pictureUrl: emp.personalInfo?.pictureUrl || undefined,
            status: emp.status === 'ACTIVE' ? 'active' : 'inactive',
            createdAt: emp.createdAt, updatedAt: emp.updatedAt
          } as PerformanceEmployee)),
          pagination: reportsResponse.pagination
        }
      }
    }
  }, [tenantDomain])

  const handleAutocompleteSelect = useCallback(async (employeeId: string) => {
    try {
      setLoadingSelectedEmployee(true)
      const employeeData = await getEmployee(tenantDomain!, employeeId)
      setSelectedEmployee(employeeId)
      setSelectedEmployeeName(employeeData.personalInfo?.name || employeeData.email || '')
      const roleId = employeeData.currentEngagement?.roleId?.toString() || null
      setSelectedEmployeeRoleId(roleId)
      const role = availableRoles.find(r => r.roleId === roleId)
      setSelectedEmployeeRoleGroup(role?.roleGroup || (employeeData.currentEngagement as any)?.roleGroup || null)
      setSelectedEmployeeRoleName(role?.name || employeeData.currentEngagement?.roleName || null)
      setSelectedEmployeePictureUrl(employeeData.personalInfo?.pictureUrl || null)
    } catch (err) {
      console.error('Failed to fetch employee:', err)
    } finally {
      setLoadingSelectedEmployee(false)
    }
  }, [tenantDomain, availableRoles])

  const handleDropdownSelect = useCallback(async (employeeId: string) => {
    if (!employeeId || !tenantDomain) return
    setSelectedEmployee(employeeId)
    setLoadingSelectedEmployee(true)
    const emp = employees.find(e => e.employeeId === employeeId)
    if (emp) {
      setSelectedEmployeeName(emp.name || emp.email || '')
      setSelectedEmployeePictureUrl(emp.pictureUrl || null)
    }
    try {
      const employeeData = await getEmployee(tenantDomain, employeeId)
      const roleId = employeeData.currentEngagement?.roleId?.toString() || null
      setSelectedEmployeeRoleId(roleId)
      const role = availableRoles.find(r => r.roleId === roleId)
      setSelectedEmployeeRoleGroup(role?.roleGroup || (employeeData.currentEngagement as any)?.roleGroup || null)
      setSelectedEmployeeRoleName(role?.name || employeeData.currentEngagement?.roleName || null)
      setSelectedEmployeePictureUrl(employeeData.personalInfo?.pictureUrl || emp?.pictureUrl || null)
    } catch (err) {
      console.error('Failed to fetch employee roleId:', err)
      setSelectedEmployeeRoleId(null)
      setSelectedEmployeeRoleGroup(null)
      setSelectedEmployeeRoleName(null)
    } finally {
      setLoadingSelectedEmployee(false)
    }
  }, [tenantDomain, employees, availableRoles])

  const loadEvaluationData = useCallback(async () => {
    if (!tenantDomain) return
    try {
      setLoading(true)
      setError(null)
      const [employeeProfile, itemsResponse, employeesResponse, categoriesResponse, configData, rolesResponse] = await Promise.all([
        getMyEmployeeProfile(tenantDomain),
        listEvaluationItems(tenantDomain, { status: 'active' }),
        loadEmployeesForEvaluatorType(evaluatorType),
        getEvaluationCategories(tenantDomain),
        getEvaluationConfig(tenantDomain),
        listRoles(tenantDomain, { isActive: true, limit: 300 })
      ])

      const userRoleId = employeeProfile.roleId || null
      setCurrentUserRole(userRoleId || 'no-role')
      setEvaluationItems(itemsResponse.items)
      setEmployees(employeesResponse.items)
      setCategories(categoriesResponse.items.sort((a, b) => a.order - b.order))
      setConfig(configData)
      setAvailableRoles(rolesResponse.roles)

      const userRole = rolesResponse.roles.find((r: Role) => r.roleId === userRoleId)
      const userRoleGroup = userRole?.roleGroup || null

      let applicableIds: string[]
      if (evaluatorType === 'self') {
        applicableIds = filterItemIdsByRole(itemsResponse.items, userRoleId, userRoleGroup)
      } else {
        applicableIds = itemsResponse.items.map(item => item.itemId)
      }
      setApplicableItemIds(applicableIds)

      const initialScores: Record<string, CreateEvaluationScoreData> = {}
      applicableIds.forEach(itemId => {
        initialScores[itemId] = { itemId, score: 0, skipped: false, skipReason: '', comments: '', evidence: '', developmentPlan: '' }
      })
      setScores(initialScores)
    } catch (err) {
      console.error('Failed to load evaluation data:', err)
      setError('Failed to load evaluation data')
    } finally {
      setLoading(false)
    }
  }, [tenantDomain, evaluatorType, loadEmployeesForEvaluatorType, filterItemIdsByRole])

  const fetchAllScores = useCallback(async (evalId: string): Promise<EvaluationScore[]> => {
    if (!tenantDomain) return []
    const allScores: EvaluationScore[] = []
    let nextToken: string | undefined
    do {
      const response = await getEvaluationScores(tenantDomain, evalId, { limit: 100, nextToken })
      allScores.push(...response.items)
      nextToken = response.pagination?.nextToken
    } while (nextToken)
    return allScores
  }, [tenantDomain])

  const loadExistingEvaluation = useCallback(async (evalId: string) => {
    if (!tenantDomain) return
    try {
      setLoading(true)
      setError(null)
      const [evaluationData, allScores, itemsResponse, categoriesResponse, configData, rolesResponse] = await Promise.all([
        getEvaluation(tenantDomain, evalId),
        fetchAllScores(evalId),
        listEvaluationItems(tenantDomain, { status: 'active' }),
        getEvaluationCategories(tenantDomain),
        getEvaluationConfig(tenantDomain),
        listRoles(tenantDomain, { isActive: true, limit: 300 })
      ])

      setEvaluation(evaluationData)
      setIsDraft(evaluationData.status === 'draft')
      setEvaluatorType(evaluationData.evaluatorType)
      setSelectedEmployee(evaluationData.employeeId)
      setEvaluationItems(itemsResponse.items)
      setCategories(categoriesResponse.items.sort((a, b) => a.order - b.order))
      setConfig(configData)
      setAvailableRoles(rolesResponse.roles)

      if (evaluationData.employeeId) {
        try {
          const employeeData = await getEmployee(tenantDomain, evaluationData.employeeId)
          const roleId = employeeData.currentEngagement?.roleId?.toString() || null
          setSelectedEmployeeRoleId(roleId)
          setSelectedEmployeeName(employeeData.personalInfo?.name || employeeData.email || '')
          setSelectedEmployeePictureUrl(employeeData.personalInfo?.pictureUrl || null)
          const role = rolesResponse.roles.find((r: Role) => r.roleId === roleId)
          setSelectedEmployeeRoleGroup(role?.roleGroup || null)
          const filteredIds = filterItemIdsByRole(itemsResponse.items, roleId, role?.roleGroup || null)
          setApplicableItemIds(filteredIds)
        } catch (err) {
          console.error('Failed to fetch employee for role filtering:', err)
          setApplicableItemIds(itemsResponse.items.map(item => item.itemId))
        }
      }

      // Convert scores
      const scoresRecord: Record<string, CreateEvaluationScoreData> = {}
      const scoresByItem: Record<string, EvaluationScore[]> = {}
      allScores.forEach(score => {
        if (!scoresByItem[score.itemId]) scoresByItem[score.itemId] = []
        scoresByItem[score.itemId].push(score)
      })
      Object.entries(scoresByItem).forEach(([itemId, itemScores]) => {
        const sorted = itemScores.sort((a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime())
        const s = sorted[0]
        scoresRecord[itemId] = {
          itemId: s.itemId, score: s.score || 0, skipped: s.skipped || false,
          skipReason: s.skipReason || '', comments: s.comments || '',
          evidence: s.evidence || '', developmentPlan: s.developmentPlan || ''
        }
      })
      scoresRef.current = scoresRecord
      setScores(scoresRecord)
      if (evaluationData.overallScore) setOverallScore(evaluationData.overallScore)

      // Enter focus mode directly for existing evaluations
      enterFocusMode()
    } catch (err) {
      console.error('Failed to load evaluation:', err)
      setError('Failed to load evaluation')
    } finally {
      setLoading(false)
    }
  }, [tenantDomain, fetchAllScores, filterItemIdsByRole])

  // ============ EFFECTS ============
  // Enter focus mode immediately when loading an existing evaluation
  useEffect(() => {
    if (evaluationId) enterFocusMode()
    return () => exitFocusMode()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (evaluationId) loadExistingEvaluation(evaluationId)
    else loadEvaluationData()
  }, [evaluationId, loadEvaluationData, loadExistingEvaluation])

  // Set initial category for existing evaluations (after categories + scores load)
  useEffect(() => {
    if (focusMode && applicableCategories.length > 0 && evaluation) {
      const idx = findFirstIncompleteCategory(scoresRef.current)
      setCurrentCategoryIndex(idx)
    }
  }, [focusMode, applicableCategories.length, evaluation, findFirstIncompleteCategory])

  useEffect(() => {
    const checkPeers = async () => {
      if (!tenantDomain) return
      setLoadingPeers(true)
      try {
        const peersResponse = await listPeerEmployees(tenantDomain, { limit: 1 })
        setHasPeers(peersResponse.items.length > 0)
      } catch { setHasPeers(false) }
      finally { setLoadingPeers(false) }
    }
    checkPeers()
  }, [tenantDomain])

  useEffect(() => {
    if (!loadingPeers && allowedEvaluatorTypes.length > 0 && !evaluation) {
      if (!allowedEvaluatorTypes.some(t => t.value === evaluatorType)) {
        setEvaluatorType(allowedEvaluatorTypes[0].value)
      }
    }
  }, [loadingPeers, allowedEvaluatorTypes, evaluation, evaluatorType])

  useEffect(() => {
    if (!evaluationId && evaluatorType !== 'self') {
      loadEmployeesForEvaluatorType(evaluatorType)
        .then(r => setEmployees(r.items as PerformanceEmployee[]))
        .catch(err => console.error('Failed to load employees:', err))
    } else if (evaluatorType === 'self') {
      setEmployees([])
      setSelectedEmployee(null)
      setSelectedEmployeeRoleId(null)
      setSelectedEmployeeRoleGroup(null)
      setSelectedEmployeePictureUrl(null)
    }
  }, [evaluatorType, evaluationId, loadEmployeesForEvaluatorType])

  useEffect(() => {
    if (evaluationId) return
    if (evaluatorType === 'self' || (!selectedEmployeeRoleId && !selectedEmployeeRoleGroup) || evaluationItems.length === 0) return
    const filteredIds = filterItemIdsByRole(evaluationItems, selectedEmployeeRoleId, selectedEmployeeRoleGroup)
    setApplicableItemIds(filteredIds)
    const currentScores = scoresRef.current
    const newScores: Record<string, CreateEvaluationScoreData> = {}
    filteredIds.forEach(itemId => {
      newScores[itemId] = currentScores[itemId] || { itemId, score: 0, skipped: false, skipReason: '', comments: '', evidence: '', developmentPlan: '' }
    })
    setScores(newScores)
  }, [evaluationId, selectedEmployeeRoleId, selectedEmployeeRoleGroup, evaluatorType, evaluationItems, filterItemIdsByRole])

  useEffect(() => {
    scoresRef.current = scores
    calculateOverallScore(scores)
  }, [scores, calculateOverallScore])

  // Auto-save
  useEffect(() => {
    if (!isDraft || !evaluation) return
    const timer = setTimeout(() => saveDraft(), 3000)
    return () => clearTimeout(timer)
  }, [scores, isDraft, evaluation])

  // ============ ACTIONS ============
  const saveDraft = async () => {
    if (!tenantDomain || !evaluation || !isDraft) return
    try {
      setSaving(true)
      const scoresArray = Object.values(scores).filter(s => s.score > 0 || s.skipped)
      if (scoresArray.length > 0) await addEvaluationScores(tenantDomain, evaluation.evaluationId, scoresArray)
      if (overallScore) await updateEvaluation(tenantDomain, evaluation.evaluationId, { overallScore })
      setLastSavedAt(new Date())
    } catch (err) {
      console.error('Failed to save draft:', err)
    } finally {
      setSaving(false)
    }
  }

  const createNewEvaluation = async () => {
    if (!tenantDomain) return
    try {
      setSaving(true)
      setError(null)
      if (evaluatorType !== 'self' && !selectedEmployee) {
        setError('Selecione um funcionário para avaliar')
        return
      }
      const payload: any = {
        evaluatorType,
        evaluationPeriod: { startDate: evaluationPeriod.startDate, endDate: evaluationPeriod.endDate },
        status: 'draft'
      }
      if (evaluatorType !== 'self') payload.employeeId = selectedEmployee
      const newEval = await saveDraftEvaluation(tenantDomain, payload)
      setEvaluation(newEval)
      setIsDraft(true)
      enterFocusMode()
      setCurrentCategoryIndex(0)
    } catch (err) {
      console.error('Failed to create evaluation:', err)
      setError('Falha ao criar avaliação')
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async () => {
    if (!tenantDomain || !evaluation) return
    const validationError = validateEvaluation()
    if (validationError) { setError(validationError); return }
    try {
      setSaving(true)
      setError(null)
      await saveDraft()
      await submitEvaluation(tenantDomain, evaluation.evaluationId)
      setIsDraft(false)
      setShowCelebration(true)
      setTimeout(() => navigate({ to: '/performance/avaliacoes' }), 3000)
    } catch (err) {
      console.error('Failed to submit evaluation:', err)
      setError('Falha ao enviar avaliação. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  const validateEvaluation = (): string | null => {
    if (!applicableItemIds || applicableItemIds.length === 0) return 'Nenhum critério de avaliação aplicável.'

    const incompleteItems = applicableItemIds.filter(itemId => {
      const score = scores[itemId]
      if (!score) return true
      if (score.skipped) return false  // N/A no longer requires justification
      return score.score < 1 || score.score > 5
    })

    if (incompleteItems.length > 0) {
      return `Faltam ${incompleteItems.length} critério(s). Atribua uma nota ou marque como "Não se Aplica".`
    }

    const threshold = config?.rules.developmentPlanThreshold || 3.0
    const lowScoresWithoutPlan = applicableItemIds.filter(itemId => {
      const score = scores[itemId]
      if (!score || score.skipped) return false
      return score.score > 0 && score.score < threshold && !score.developmentPlan?.trim()
    })
    if (lowScoresWithoutPlan.length > 0) {
      return `${lowScoresWithoutPlan.length} critério(s) com nota abaixo de ${threshold} requerem plano de desenvolvimento.`
    }

    const scoredCount = applicableItemIds.filter(id => { const s = scores[id]; return s && !s.skipped && s.score > 0 }).length
    if (scoredCount === 0) return 'Pelo menos um critério deve ser avaliado com nota.'
    if (!overallScore) return 'Pontuação geral não calculada.'
    return null
  }

  const handleScoreChange = (itemId: string, score: number) => {
    setScores(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { itemId, score: 0, skipped: false, skipReason: '', comments: '', evidence: '', developmentPlan: '' }), score, skipped: false, skipReason: '' }
    }))
    // Auto-expand if low score
    const threshold = config?.rules.developmentPlanThreshold || 3.0
    if (score > 0 && score < threshold) {
      setExpandedItems(prev => new Set(prev).add(itemId))
    }
  }

  const handleSkip = (itemId: string) => {
    setScores(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { itemId, score: 0, skipped: false, skipReason: '', comments: '', evidence: '', developmentPlan: '' }), score: 0, skipped: true, skipReason: '' }
    }))
    setExpandedItems(prev => { const n = new Set(prev); n.delete(itemId); return n })
  }

  const handleUnskip = (itemId: string) => {
    setScores(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { itemId, score: 0, skipped: false, skipReason: '', comments: '', evidence: '', developmentPlan: '' }), score: 0, skipped: false, skipReason: '' }
    }))
  }

  const handleCommentChange = (itemId: string, field: keyof CreateEvaluationScoreData, value: string) => {
    setScores(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { itemId, score: 0, comments: '', evidence: '', developmentPlan: '' }), [field]: value }
    }))
  }

  const toggleExpand = (itemId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  const autoFillCategory = (categoryId: string, score: number) => {
    const themeItems = getItemsForTheme(categoryId)
    setScores(prev => {
      const updated = { ...prev }
      themeItems.forEach(item => {
        const existing = prev[item.itemId] || { itemId: item.itemId, score: 0, comments: '', evidence: '', developmentPlan: '' }
        updated[item.itemId] = {
          ...existing, score,
          comments: existing.comments || 'Auto-preenchido para teste',
          developmentPlan: score < (config?.rules.developmentPlanThreshold || 3.0)
            ? (existing.developmentPlan || 'Plano de desenvolvimento a ser detalhado')
            : existing.developmentPlan
        }
      })
      return updated
    })
  }

  // ============ NAVIGATION ============
  const showCategory = (idx: number) => {
    setCurrentCategoryIndex(idx)
    setError(null)
    if (mainContentRef.current) mainContentRef.current.scrollTop = 0
  }

  const prevCategory = () => { if (currentCategoryIndex > 0) showCategory(currentCategoryIndex - 1) }
  const nextCategory = () => { if (currentCategoryIndex <= applicableCategories.length - 1) showCategory(currentCategoryIndex + 1) }

  const getPrevLabel = () => {
    if (currentCategoryIndex <= 0) return 'Anterior'
    if (currentCategoryIndex <= applicableCategories.length - 1) return `← ${applicableCategories[currentCategoryIndex - 1].name}`
    return `← ${applicableCategories[applicableCategories.length - 1].name}`
  }

  const getNextLabel = () => {
    if (currentCategoryIndex >= applicableCategories.length) return ''
    if (currentCategoryIndex === applicableCategories.length - 1) return 'Revisão →'
    return `${applicableCategories[currentCategoryIndex + 1].name} →`
  }

  // Keyboard nav
  useEffect(() => {
    if (!focusMode) return
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'TEXTAREA' || (e.target as HTMLElement).tagName === 'INPUT') return
      if (e.key === 'ArrowRight') { e.preventDefault(); nextCategory() }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prevCategory() }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [focusMode, currentCategoryIndex, applicableCategories.length])

  // ============ LOADING / ERROR / CELEBRATION ============
  if (loading) return <FormularioAvaliacaoSkeleton />

  if (error && evaluationItems.length === 0 && !loading) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="bg-brand-dark-light border-error/30 max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-error mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Erro</h2>
            <p className="text-brand-gray mb-6">{error}</p>
            <Button onClick={() => window.history.length > 1 ? window.history.back() : navigate({ to: '/performance/avaliacoes' })} variant="secondary">
              <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (showCelebration) {
    return (
      <div className="fixed inset-0 bg-brand-dark flex items-center justify-center z-50 animate-fadeIn">
        <div className="text-center px-4">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-brand-primary rounded-monumenta mb-6 animate-bounce">
            <PartyPopper className="w-12 h-12 text-black" />
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">Avaliação Concluída!</h1>
          <p className="text-xl text-brand-gray mb-2">Sua avaliação foi registrada com sucesso</p>
          <div className="flex items-center justify-center gap-2 text-brand-primary">
            <Sparkles className="w-5 h-5" />
            <span className="text-lg">Redirecionando...</span>
          </div>
        </div>
      </div>
    )
  }

  // ============================================================
  // STEP 1: TYPE SELECTOR (before focus mode)
  // ============================================================
  if (!focusMode) {
    const evalTypeLabel = evaluatorTypeInfo[evaluatorType]?.label || evaluatorType
    const cycleName = `${new Date(evaluationPeriod.startDate).toLocaleDateString('pt-BR', { month: 'short' })} – ${new Date(evaluationPeriod.endDate).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}`
    const canStart = evaluatorType === 'self' || !!selectedEmployee

    return (
      <div className="min-h-[80vh] flex items-center justify-center p-4">
        <div className="w-full max-w-xl text-center">
          <h1 className="text-3xl font-display font-bold text-white mb-2">
            {evaluation ? 'Continuar Avaliação' : 'Nova Avaliação'}
          </h1>
          <p className="text-brand-gray mb-8">Selecione o tipo de avaliação que deseja realizar</p>

          {/* Type selection -- big buttons */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            {allowedEvaluatorTypes.map(type => {
              const info = evaluatorTypeInfo[type.value]
              const TypeIcon = evaluatorTypeIcons[type.value] || UserRound
              const isSelected = evaluatorType === type.value
              return (
                <button
                  key={type.value}
                  onClick={() => !evaluation && setEvaluatorType(type.value)}
                  disabled={!!evaluation}
                  className={`p-6 rounded-xl border-2 text-center transition-all ${
                    isSelected
                      ? 'border-brand-primary bg-brand-primary/10'
                      : 'border-brand-gray/20 bg-brand-dark-light hover:border-brand-primary/50 hover:-translate-y-0.5'
                  } ${evaluation ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <TypeIcon className={`w-8 h-8 mx-auto mb-2 ${isSelected ? 'text-brand-primary' : 'text-brand-gray'}`} />
                  <div className="text-white font-bold mb-1">{info?.label}</div>
                  <div className="text-xs text-brand-gray">{info?.desc}</div>
                </button>
              )
            })}
          </div>

          {/* Cycle info (hidden/pre-filled) */}
          <div className="flex items-center justify-center gap-2 px-5 py-3 bg-brand-dark-light rounded-lg mb-6 text-sm text-brand-gray">
            <Calendar className="w-4 h-4" /> Ciclo ativo: <strong className="text-white">{cycleName}</strong>
          </div>

          {/* Employee selection (only for non-self) */}
          {evaluatorType !== 'self' && !evaluation && (
            <div className="text-left mb-6">
              <Label className="text-white text-sm mb-2 block">Funcionário a avaliar</Label>

              {(evaluatorType !== 'peer' && (evaluatorType === 'hr' || shouldUseSearchMode)) ? (
                <div className="space-y-3">
                  {!selectedEmployee ? (
                    <>
                      <EmployeeAutocomplete
                        onSelect={handleAutocompleteSelect}
                        navigateOnSelect={false}
                        placeholder="Buscar por nome ou email..."
                        disabled={!!evaluation}
                      />
                      {loadingSelectedEmployee && (
                        <div className="flex items-center gap-2 text-brand-gray">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Carregando...</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="p-3 bg-success/10 border border-success/30 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-success" />
                        <span className="text-white">{selectedEmployeeName}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => {
                        setSelectedEmployee(null); setSelectedEmployeeName('');
                        setSelectedEmployeeRoleId(null); setSelectedEmployeeRoleGroup(null);
                        setSelectedEmployeeRoleName(null); setSelectedEmployeePictureUrl(null);
                      }} className="text-brand-gray hover:text-white">Alterar</Button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {evaluation && selectedEmployee && selectedEmployeeName ? (
                    <div className="p-3 bg-success/10 border border-success/30 rounded-lg flex items-center gap-2">
                      <Check className="w-5 h-5 text-success" />
                      <span className="text-white">{selectedEmployeeName}</span>
                    </div>
                  ) : (
                    <Select value={selectedEmployee || ''} onValueChange={handleDropdownSelect} disabled={!!evaluation || employees.length === 0}>
                      <SelectTrigger className="form-select bg-brand-dark-lighter border-brand-gray/10 text-white h-12">
                        <SelectValue placeholder={employees.length === 0 ? 'Nenhum funcionário disponível' : 'Selecione o funcionário'} />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => {
                          const isUUID = emp.role && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(emp.role)
                          return (
                            <SelectItem key={emp.employeeId} value={emp.employeeId}>
                              <div className="flex items-center gap-3">
                                <Avatar className="w-8 h-8 flex-shrink-0">
                                  {emp.pictureUrl && <AvatarImage src={emp.pictureUrl} alt={emp.name || emp.email} />}
                                  <AvatarFallback className="bg-brand-primary/20 text-brand-primary text-xs">
                                    {(emp.name || emp.email || '?').charAt(0).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <span>{emp.name || emp.email}{!isUUID && emp.role ? ` - ${emp.role}` : ''}</span>
                              </div>
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  )}
                  {loadingSelectedEmployee && (
                    <div className="flex items-center gap-2 mt-2 text-brand-gray">
                      <Loader2 className="w-4 h-4 animate-spin" /> <span className="text-sm">Carregando...</span>
                    </div>
                  )}
                </>
              )}

              {/* Employee info card */}
              {selectedEmployee && !loadingSelectedEmployee && selectedEmployeeRoleName && (
                <div className="mt-3 p-3 bg-brand-primary/10 border border-brand-primary/30 rounded-lg text-sm">
                  <div className="flex items-center gap-2 text-brand-gray">
                    <span className="text-brand-gray/70">Cargo:</span>
                    <span className="text-white">{selectedEmployeeRoleName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-brand-gray">
                    <span className="text-brand-gray/70">Critérios:</span>
                    <span className="text-brand-primary font-medium">{applicableItemIds.length} aplicáveis</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <LoadingButton
            onClick={createNewEvaluation}
            disabled={!canStart || loadingPeers}
            loading={saving}
            variant="primary"
            size="lg"
            className="px-8"
          >
            {saving ? 'Criando...' : 'Comecar Avaliacao'}
          </LoadingButton>
        </div>
      </div>
    )
  }

  // ============================================================
  // FOCUS MODE: Eval Sidebar + Category Pages
  // ============================================================
  const { done: progressDone, total: progressTotal } = getOverallProgress()
  const progressPct = progressTotal > 0 ? Math.round((progressDone / progressTotal) * 100) : 0
  const isReviewPage = currentCategoryIndex >= applicableCategories.length
  const evalTypeLabel = evaluatorTypeInfo[evaluatorType]?.label || evaluatorType

  // Render current category content
  const renderCategoryContent = () => {
    if (isReviewPage) return renderReview()

    const cat = applicableCategories[currentCategoryIndex]
    if (!cat) return null

    const Icon = getIconComponent(cat.icon)
    const colorScheme = cat.colorScheme || { bgClass: 'bg-brand-primary/20', textClass: 'text-brand-primary' }
    const themeItems = getItemsForTheme(cat.categoryId)
    const { done, total } = getCatProgress(cat)
    const threshold = config?.rules.developmentPlanThreshold || 3.0

    return (
      <>
        {/* Category header */}
        <div className="flex items-center gap-4 mb-6">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorScheme.bgClass}`}>
            <Icon className={`w-6 h-6 ${colorScheme.textClass}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-display font-bold text-white">{cat.name}</h2>
            {cat.description && <p className="text-sm text-brand-gray truncate">{cat.description}</p>}
          </div>
          <div className="text-sm text-brand-gray bg-brand-dark-light px-4 py-2 rounded-full whitespace-nowrap">
            {done} de {total} concluídos
          </div>
        </div>

        {/* Admin auto-fill */}
        {isDraft && isAdmin && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Sparkles className="w-4 h-4 text-brand-primary" />
            <span className="text-xs text-brand-gray">Auto-preencher:</span>
            {[1, 2, 3, 4, 5].map(v => (
              <button
                key={v}
                onClick={() => autoFillCategory(cat.categoryId, v)}
                className="px-2.5 py-1 text-xs font-medium border border-brand-gray/20 rounded-md text-brand-gray
                  hover:bg-brand-primary hover:text-black hover:border-brand-primary transition-all"
              >
                <Star className="w-3 h-3 inline" />{v}
              </button>
            ))}
          </div>
        )}

        {/* Criteria rows */}
        <div className="space-y-1">
          {themeItems.map((item, index) => {
            const score = scores[item.itemId] || { score: 0, skipped: false, skipReason: '', comments: '', evidence: '', developmentPlan: '' }
            const isNA = !!score.skipped
            const scored = score.score > 0
            const isLow = scored && score.score < threshold
            const devPlanMissing = isLow && !score.developmentPlan?.trim()
            const isExpanded = expandedItems.has(item.itemId) || isLow || (scored && !!score.comments)

            // Determine badge
            let badgeClass = 'bg-brand-dark-lighter text-brand-gray'
            let badgeContent: React.ReactNode = index + 1
            if (isNA) { badgeClass = 'bg-brand-gray/20 text-brand-gray'; badgeContent = '—' }
            else if (devPlanMissing) { badgeClass = 'bg-warning text-black'; badgeContent = <AlertCircle className="w-3.5 h-3.5" /> }
            else if (scored) { badgeClass = 'bg-success/20 text-success'; badgeContent = <Check className="w-3.5 h-3.5" /> }

            return (
              <div
                key={item.itemId}
                className={`rounded-xl bg-brand-dark-light p-4 transition-all ${isNA ? 'opacity-50' : ''} ${
                  devPlanMissing ? 'ring-1 ring-warning/40' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Badge */}
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${badgeClass}`}>
                    {badgeContent}
                  </div>

                  {/* Body */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white leading-relaxed mb-2">{item.description}</p>

                    {/* Score controls */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <CompactScoreInput
                        value={score.score}
                        skipped={score.skipped}
                        onChange={(s) => handleScoreChange(item.itemId, s)}
                        onSkip={() => handleSkip(item.itemId)}
                        onUnskip={() => handleUnskip(item.itemId)}
                        disabled={!isDraft}
                        scoreLabels={config?.scoreLabels}
                      />

                      {!isNA && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpand(item.itemId)}
                          className="ml-auto text-xs text-brand-gray hover:text-white gap-1 px-2 py-1"
                        >
                          <Pencil className="w-3 h-3" />
                          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </Button>
                      )}
                    </div>

                    {/* Expanded fields */}
                    {!isNA && isExpanded && (
                      <div className="mt-3 pt-3 border-t border-brand-gray/10 space-y-3">
                        <div>
                          <label className="text-xs font-semibold text-brand-gray uppercase tracking-wide mb-1 block">
                            Comentários / Evidências
                          </label>
                          <Textarea
                            value={score.comments || ''}
                            onChange={(e) => handleCommentChange(item.itemId, 'comments', e.target.value)}
                            placeholder="Descreva evidências observadas..."
                            className="form-input bg-brand-dark border-brand-gray/10 text-white min-h-[60px] text-sm"
                            disabled={!isDraft}
                          />
                        </div>

                        {isLow && (
                          <div>
                            <label className="text-xs font-semibold text-warning uppercase tracking-wide mb-1 block">
                              Plano de Desenvolvimento <span className="text-error">*</span>
                            </label>
                            <Textarea
                              value={score.developmentPlan || ''}
                              onChange={(e) => handleCommentChange(item.itemId, 'developmentPlan', e.target.value)}
                              placeholder="Descreva ações para desenvolvimento..."
                              className={`form-input bg-brand-dark text-white min-h-[60px] text-sm ${devPlanMissing ? 'border-warning/50' : 'border-brand-gray/10'}`}
                              disabled={!isDraft}
                            />
                            {devPlanMissing && (
                              <p className="text-warning text-xs mt-1 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" /> Obrigatório para notas abaixo de {threshold}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </>
    )
  }

  // Review page
  const renderReview = () => {
    const allThemeScores = applicableCategories.map(theme => {
      const themeItems = getItemsForTheme(theme.categoryId)
      const scoredItems = themeItems.filter(item => { const s = scores[item.itemId]; return s && !s.skipped && s.score > 0 })
      const skippedItems = themeItems.filter(item => scores[item.itemId]?.skipped)
      const avgScore = scoredItems.length > 0 ? scoredItems.reduce((sum, item) => sum + scores[item.itemId].score, 0) / scoredItems.length : 0
      return { theme, avgScore, scoredCount: scoredItems.length, skippedCount: skippedItems.length, totalItems: themeItems.length, completedItems: scoredItems.length + skippedItems.length }
    })

    const validationErr = validateEvaluation()

    return (
      <>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-success/20">
            <Award className="w-6 h-6 text-success" />
          </div>
          <div>
            <h2 className="text-xl font-display font-bold text-white">Revisão Final</h2>
            <p className="text-sm text-brand-gray">Confira o resumo antes de registrar</p>
          </div>
        </div>

        {validationErr && (
          <Alert variant="warning" className="mb-6">
            <AlertTitle>Avaliacao Incompleta</AlertTitle>
            <AlertDescription>
              {validationErr}
              <p className="text-xs mt-1 opacity-70">Use a barra lateral para navegar ate os itens pendentes</p>
            </AlertDescription>
          </Alert>
        )}

        {/* Overall Score */}
        {overallScore && (
          <div className="bg-gradient-to-br from-brand-primary/20 to-brand-dark-light border border-brand-primary/30 rounded-xl p-6 text-center mb-6">
            <p className="text-brand-gray text-sm mb-1">Pontuação Geral</p>
            <div className="text-4xl font-bold text-brand-primary">
              {overallScore.toFixed(1)}<span className="text-xl text-brand-gray">/5.0</span>
            </div>
          </div>
        )}

        {/* Theme breakdown */}
        <div className="space-y-3 mb-6">
          {allThemeScores.map(({ theme, avgScore, scoredCount, skippedCount, totalItems, completedItems }) => {
            const Icon = getIconComponent(theme.icon)
            const colorScheme = theme.colorScheme || { bgClass: 'bg-brand-primary/20', textClass: 'text-brand-primary' }
            const isComplete = completedItems === totalItems
            return (
              <div
                key={theme.categoryId}
                className={`bg-brand-dark-light rounded-xl p-4 flex items-center justify-between border ${isComplete ? 'border-success/20' : 'border-warning/20'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${colorScheme.bgClass} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${colorScheme.textClass}`} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{theme.name}</h3>
                    <p className="text-xs text-brand-gray">
                      {scoredCount} avaliados{skippedCount > 0 && `, ${skippedCount} N/A`} / {totalItems} total
                    </p>
                  </div>
                </div>
                <div className="text-xl font-bold text-brand-primary">
                  {scoredCount > 0 ? avgScore.toFixed(1) : '—'}
                </div>
              </div>
            )
          })}
        </div>

        {/* Warning */}
        <Alert variant="info" className="mb-6">
          <AlertTitle>Antes de enviar</AlertTitle>
          <AlertDescription>Apos o envio, esta avaliacao nao podera mais ser editada.</AlertDescription>
        </Alert>

        {/* Submit */}
        <LoadingButton
          onClick={handleSubmit}
          disabled={!!validationErr}
          loading={saving}
          variant="primary"
          size="lg"
          className="w-full bg-success hover:bg-success/90 gap-2"
        >
          <CheckCircle className="w-5 h-5" /> Registrar Avaliacao
        </LoadingButton>
      </>
    )
  }

  return (
    <div className="min-h-screen bg-brand-dark flex">
      {/* ===== EVAL SIDEBAR ===== */}
      <aside className="w-60 min-w-[240px] bg-brand-dark-light border-r border-brand-gray/10 flex flex-col h-full hidden lg:flex">
        {/* Header */}
        <div className="p-4 border-b border-brand-gray/10">
          <h2 className="text-xs font-semibold text-brand-gray uppercase tracking-wider mb-3">Avaliacao</h2>
          <div className="space-y-1 text-xs">
            <div className="flex items-center gap-2 text-brand-gray">
              {React.createElement(evaluatorTypeIcons[evaluatorType] || UserRound, { className: 'w-3.5 h-3.5' })}
              <span className="text-white font-medium">{evalTypeLabel}</span>
            </div>
            {selectedEmployeeName && (
              <div className="flex items-center gap-2 text-brand-gray">
                <User className="w-3.5 h-3.5" />
                <span className="text-white font-medium truncate">{selectedEmployeeName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="px-4 py-3 border-b border-brand-gray/10">
          <div className="flex justify-between text-xs text-brand-gray mb-2">
            <span>Progresso</span>
            <span>{progressDone} de {progressTotal}</span>
          </div>
          <Progress value={progressPct} className="h-1.5 bg-brand-dark-lighter" />
        </div>

        {/* Category nav */}
        <nav className="flex-1 overflow-y-auto py-2">
          {applicableCategories.map((cat, ci) => {
            const { done, total } = getCatProgress(cat)
            const isActive = ci === currentCategoryIndex && !isReviewPage
            const isComplete = done === total
            const Icon = getIconComponent(cat.icon)
            return (
              <button
                key={cat.categoryId}
                onClick={() => showCategory(ci)}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-all border-l-[3px] ${
                  isActive
                    ? 'border-brand-primary bg-brand-primary/10'
                    : 'border-transparent hover:bg-brand-dark-lighter'
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-brand-primary' : 'text-brand-gray'}`} />
                <span className={`text-sm flex-1 truncate ${isActive ? 'text-white font-medium' : 'text-brand-gray'}`}>
                  {cat.name}
                </span>
                <Badge variant={isComplete ? 'success' : done > 0 ? 'info' : 'default'} className="text-[10px] px-2 py-0.5">
                  {done}/{total}
                </Badge>
              </button>
            )
          })}
          {/* Review nav item */}
          <button
            onClick={() => showCategory(applicableCategories.length)}
            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-all border-l-[3px] ${
              isReviewPage ? 'border-brand-primary bg-brand-primary/10' : 'border-transparent hover:bg-brand-dark-lighter'
            }`}
          >
            <Award className={`w-4 h-4 flex-shrink-0 ${isReviewPage ? 'text-brand-primary' : 'text-brand-gray'}`} />
            <span className={`text-sm ${isReviewPage ? 'text-white font-medium' : 'text-brand-gray'}`}>Revisão</span>
          </button>
        </nav>

        {/* Exit */}
        <div className="p-4 border-t border-brand-gray/10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/performance/avaliacoes' })}
            className="w-full gap-2"
          >
            <X className="w-4 h-4" /> Sair da Avaliacao
          </Button>
        </div>
      </aside>

      {/* ===== MOBILE TOP BAR ===== */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-brand-dark/95 backdrop-blur-sm border-b border-brand-gray/10 z-20">
        <div className="flex gap-1.5 px-3 py-2 overflow-x-auto">
          {applicableCategories.map((cat, ci) => {
            const { done, total } = getCatProgress(cat)
            const isActive = ci === currentCategoryIndex && !isReviewPage
            const isComplete = done === total
            return (
              <button
                key={cat.categoryId}
                onClick={() => showCategory(ci)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  isActive
                    ? 'bg-brand-primary/15 border-brand-primary text-brand-primary'
                    : isComplete
                    ? 'bg-success/15 border-transparent text-success'
                    : 'bg-brand-dark-light border-brand-gray/20 text-brand-gray'
                }`}
              >
                {cat.name}
              </button>
            )
          })}
          <button
            onClick={() => showCategory(applicableCategories.length)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-1 ${
              isReviewPage ? 'bg-brand-primary/15 border-brand-primary text-brand-primary' : 'bg-brand-dark-light border-brand-gray/20 text-brand-gray'
            }`}
          >
            <Check className="w-3 h-3" /> Revisao
          </button>
        </div>
      </div>

      {/* ===== MAIN CONTENT ===== */}
      <main className="flex-1 overflow-y-auto" ref={mainContentRef}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 pt-4 lg:pt-8 pb-28">
          {/* Save status */}
          <div className="flex items-center justify-end gap-3 mb-4 text-xs">
            {saving && (
              <span className="text-brand-primary flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Salvando...
              </span>
            )}
            {lastSavedAt && !saving && (
              <Badge variant="success" className="gap-1">
                <Check className="w-3 h-3" /> Salvo as {lastSavedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </Badge>
            )}
            {isDraft && evaluation && (
              <Badge variant="warning" className="gap-1">
                <Pencil className="w-3 h-3" /> Rascunho
              </Badge>
            )}
          </div>

          {/* Error */}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Content */}
          <div className="animate-fadeIn" key={currentCategoryIndex}>
            {renderCategoryContent()}
          </div>
        </div>
      </main>

      {/* ===== BOTTOM NAV ===== */}
      <div className="fixed bottom-0 left-0 lg:left-60 right-0 bg-brand-dark/95 backdrop-blur-sm border-t border-brand-gray/10 px-4 sm:px-6 py-3 z-20">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={prevCategory}
            disabled={currentCategoryIndex === 0}
            className="gap-2 max-w-[40%] truncate"
          >
            {getPrevLabel()}
          </Button>

          <span className="text-xs text-brand-gray hidden sm:block">
            {saving ? (
              <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Salvando...</span>
            ) : `${progressDone}/${progressTotal} concluidos`}
          </span>

          {!isReviewPage && (
            <Button
              variant="primary"
              size="sm"
              onClick={nextCategory}
              className="gap-2 max-w-[40%] truncate"
            >
              {getNextLabel()}
            </Button>
          )}
          {isReviewPage && <div />}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.25s ease-out; }
      `}</style>
    </div>
  )
}

export default FormularioAvaliacao
